import { useEffect } from "react";
import { Button } from "../../design/primitives";
import { useNavigationGuard } from "./NavigationGuard";
import { StudioActions } from "./StudioActions";
import { useStudio } from "./StudioContext";
import css from "./studio.module.css";

/**
 * The action zone for the two screens that edit the creator record.
 *
 * Lists and Tiers edit the **same** record, so they declare the same actions.
 * Written once: a SAVE that meant something different depending on which tab
 * was open would be worse than no SAVE at all, and two copies of this is how
 * that starts.
 */
export function CreatorActions() {
  const { save, discard, saving, dirty, error, savedAt } = useStudio();
  const { register } = useNavigationGuard();

  // Tell the guard what would be lost. Registered by effect and cleared on
  // unmount, so a screen that has gone cannot hold a navigation hostage.
  useEffect(() => {
    register({ dirty, save, discard, saveLabel: "SAVE CHANGES" });
    return () => register(null);
  }, [register, dirty, save, discard]);

  /*
   * FR-10's precedence: failure, saving, unsaved, last saved. A failure
   * outranks "unsaved changes" because the two together read as merely
   * pending, which is the opposite of what happened.
   */
  const status = error ? (
    <span className={css.refuse}>COULD NOT SAVE · {error.toUpperCase()}</span>
  ) : saving ? (
    <span className={css.label}>SAVING…</span>
  ) : dirty ? (
    <span className={css.label}>UNSAVED CHANGES</span>
  ) : savedAt ? (
    <span className={css.confirm}>
      SAVED {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  ) : null;

  return (
    <StudioActions status={status}>
      <Button
        variant="secondary"
        size="rail"
        onClick={discard}
        // Nothing to discard is not a state worth offering an action for.
        disabled={!dirty || saving}
      >
        DISCARD CHANGES
      </Button>
      <Button variant="gold" size="rail" onClick={() => void save()} disabled={!dirty || saving}>
        {saving ? "SAVING…" : "SAVE CHANGES"}
      </Button>
    </StudioActions>
  );
}
